export type 頁面分類 =
  | "首頁"
  | "分類頁"
  | "內容頁"
  | "產品頁"
  | "案例頁"
  | "品牌頁"
  | "產品分類頁"
  | "評價頁";

export type 處理優先度 = "優先處理" | "後續補強" | "目前穩定";
export type 問題層級 = "此頁問題" | "全站問題";
export type 問題分類 =
  | "回答能力"
  | "可引用結構"
  | "可信度"
  | "主題對準"
  | "追蹤營運"
  | "結果觀察";

export interface 問題卡片 {
  key: string;
  title: string;
  category: 問題分類;
  scope: 問題層級;
  priority: 處理優先度;
  problem: string;
  howToFix: string[];
}

function 建立問題卡片(
  key: string,
  title: string,
  category: 問題分類,
  priority: 處理優先度,
  problem: string,
  howToFix: string[],
  scope: 問題層級 = "此頁問題"
): 問題卡片 {
  return { key, title, category, priority, scope, problem, howToFix };
}

function 是全站問題(issue: string): boolean {
  return /menu heading|導覽 heading|導覽.*噪音|全站|模板|選單|共用區塊|footer|header/i.test(issue);
}

function 需要作者(issue: string): boolean {
  return /作者資訊|作者與 schema|作者訊號/.test(issue);
}

function 需要背書(issue: string): boolean {
  return /審稿|背書|品牌實體|可信度/.test(issue);
}

function 需要日期(issue: string): boolean {
  return /日期|更新/.test(issue);
}

function 需要結構(issue: string): boolean {
  return /FAQ|清單|表格|抽取|結構化|可見內容/.test(issue);
}

function 需要主題(issue: string): boolean {
  return /title|H1|主題|摘要|小標|過短|資訊密度|意圖|信心偏低/.test(issue);
}

function 需要追蹤(issue: string): boolean {
  return /追蹤|AI Overview|citation|引用|實績|來源 URL/.test(issue);
}

function 頁型名稱(pageClassification: 頁面分類): string {
  return pageClassification;
}

function 建立作者卡片(pageClassification: 頁面分類): 問題卡片 {
  const pageName = 頁型名稱(pageClassification);
  const isContent = pageClassification === "內容頁";
  return 建立問題卡片(
    "author",
    isContent ? "作者資訊不清楚" : `${pageName}負責單位不清楚`,
    "可信度",
    isContent ? "優先處理" : "後續補強",
    isContent
      ? "這一頁沒有清楚標示作者，讀者與 AI 不容易判斷內容由誰撰寫。"
      : `這個${pageName}沒有清楚標示負責品牌、內容團隊或維護單位，可信度訊號不足。`,
    [
      "在頁面前段固定顯示撰寫或維護單位。",
      "前台名稱、系統作者與 schema author 要使用同一套名稱。",
      "雙品牌內容需明確標示屬於鴻鼎菓子、瞇瞇眼，或共同品牌內容。",
    ]
  );
}

function 建立背書卡片(pageClassification: 頁面分類): 問題卡片 {
  const isProduct = pageClassification === "產品頁";
  return 建立問題卡片(
    "brand-proof",
    isProduct ? "產品頁缺少品牌與商品證據" : "缺少品牌背書訊號",
    "可信度",
    isProduct ? "優先處理" : "後續補強",
    isProduct
      ? "這個產品頁沒有把規格、保存、成分、配送或品牌承諾整理清楚，AI 與使用者都難以判斷商品可信度。"
      : "頁面缺少可驗證的品牌、來源、門市、製程、媒體或顧客證據，內容可信度不足。",
    [
      "補上品牌實體、產品規格、保存方式、配送條件與成分資訊。",
      "需要引用品牌承諾時，使用可驗證的門市、官網、媒體、檢驗或顧客案例作為證據。",
      "雙品牌共用內容要標示各自適用範圍，避免把鴻鼎菓子與瞇瞇眼的商品訊號混在一起。",
    ]
  );
}

function 建立日期卡片(pageClassification: 頁面分類): 問題卡片 {
  return 建立問題卡片(
    "freshness",
    `${頁型名稱(pageClassification)}缺少更新資訊`,
    "可信度",
    pageClassification === "產品頁" || pageClassification === "內容頁" ? "優先處理" : "後續補強",
    "頁面沒有清楚標示發布或更新時間，使用者與 AI 不容易判斷價格、保存、配送或活動資訊是否仍然有效。",
    [
      "在頁面固定顯示最近更新日期。",
      "價格、配送、節慶檔期、保存資訊變更時同步更新頁面日期。",
      "schema dateModified 要與前台可見日期一致。",
    ]
  );
}

function 建立結構卡片(pageClassification: 頁面分類): 問題卡片 {
  const isProduct = pageClassification === "產品頁";
  return 建立問題卡片(
    "extractable-structure",
    isProduct ? "產品頁缺少容易引用的規格區塊" : "缺少容易被引用的內容結構",
    "可引用結構",
    "優先處理",
    isProduct
      ? "產品頁缺少清楚的規格、價格、保存、配送或常見問題區塊，AI 很難直接擷取答案。"
      : "頁面缺少 FAQ、清單、表格或摘要句，AI 與搜尋結果較難直接抽出答案。",
    [
      "把重點整理成摘要、清單、表格或 FAQ。",
      "產品頁至少拆出規格、口味、保存、配送、適合情境與常見問題。",
      "內容頁要把問題答案放在段落開頭，不要只寫敘事型長文。",
    ]
  );
}

function 建立主題卡片(pageClassification: 頁面分類): 問題卡片 {
  return 建立問題卡片(
    "topic-focus",
    `${頁型名稱(pageClassification)}主題不夠集中`,
    "主題對準",
    "優先處理",
    "頁面的 title、H1、摘要或正文主題訊號不夠一致，搜尋意圖與 AI 引用主題會被稀釋。",
    [
      "讓 title、H1、首段摘要對準同一個搜尋需求。",
      "一頁優先服務一個主要意圖，延伸需求用小標或 FAQ 承接。",
      "雙品牌頁面要明確區分品牌、商品線與送禮情境。",
    ]
  );
}

function 建立追蹤卡片(): 問題卡片 {
  return 建立問題卡片(
    "tracking-coverage",
    "應服務的搜尋意圖尚未完整追蹤",
    "追蹤營運",
    "後續補強",
    "頁面可服務的意圖沒有完整進入監控清單，後續無法判斷 SEO/AEO 表現是否改善。",
    [
      "把頁面推導出的核心意圖加入關鍵字監控。",
      "對送禮、品牌、商品、價格、保存、配送與在地需求分開追蹤。",
      "同時觀察 Google AI Overview 與 LLM 品牌提及，不只看自然排名。",
    ]
  );
}

function 建立導覽卡片(): 問題卡片 {
  return 建立問題卡片(
    "site-noise",
    "全站導覽訊號干擾主內容",
    "回答能力",
    "優先處理",
    "頁面模板或導覽區塊的 heading 訊號過高，可能讓 AI 與搜尋引擎誤判主內容重點。",
    [
      "降低選單、footer、共用區塊的 heading 層級。",
      "保留頁面主內容的 H1/H2 作為主要語意訊號。",
      "同一套模板修改後要抽查兩個品牌的代表頁。",
    ],
    "全站問題"
  );
}

export function 建立問題卡片清單(input: {
  pageClassification: 頁面分類;
  issues: string[];
}): 問題卡片[] {
  const cards: 問題卡片[] = [];
  const seen = new Set<string>();

  const push = (card: 問題卡片) => {
    if (seen.has(card.key)) return;
    seen.add(card.key);
    cards.push(card);
  };

  for (const issue of input.issues) {
    if (是全站問題(issue)) push(建立導覽卡片());
    if (需要作者(issue)) push(建立作者卡片(input.pageClassification));
    if (需要背書(issue)) push(建立背書卡片(input.pageClassification));
    if (需要日期(issue)) push(建立日期卡片(input.pageClassification));
    if (需要結構(issue)) push(建立結構卡片(input.pageClassification));
    if (需要主題(issue)) push(建立主題卡片(input.pageClassification));
    if (需要追蹤(issue)) push(建立追蹤卡片());
  }

  if (cards.length === 0) {
    push(
      建立問題卡片(
        "stable",
        "目前沒有明顯阻斷項",
        "結果觀察",
        "目前穩定",
        "目前沒有偵測到會直接阻斷 SEO/AEO 監控判讀的問題。",
        ["維持目前頁面結構，後續用實際排名、AI 引用與品牌提及資料驗證。"]
      )
    );
  }

  return cards;
}
